/*
  Matcha Finder — quiz navigation + scoring.

  Data-honesty rules this file follows (see sections/matcha-finder.liquid for
  where the data comes from):
    - A rating of `null` means "not recorded". It is never treated as 0 and
      never treated as "suitable". It simply earns no points for that
      question, for that product.
    - An explicit rating of 0 is a real value and is used normally.
    - Every scoring question has a fixed, small points pool (see POOL below).
      A product's score is the SUM of points it can prove, never a ratio/
      percentage of "points earned out of points possible for this product".
      That keeps a product with lots of missing data from ever looking
      better than one with more (but slightly less favourable) real data.
    - Preparation status words outside the recognised set are never treated
      as suitable — they're logged as a data warning instead (visible only
      in the theme editor's debug panel).
    - If the top candidates are too close / too thin on data to tell apart,
      the quiz says so instead of picking one with false confidence.

  No external libraries. Works with keyboard only. Respects
  prefers-reduced-motion for the one non-essential animation (progress bar).
*/

(function () {
  'use strict';

  var POOL = { bitterness: 45, flavour: 40, finish: 15 };
  var PREP_RANK = { Best: 3, Great: 2, Good: 1 };
  var BITTERNESS_TARGET = { enjoy: 4.5, little: 3, gentle: 1.5, least: 0.5 };
  var FINISH_DRY_TARGET = 4;
  var FINISH_SWEET_TARGET = 4.5;
  var ATTR_LABEL = {
    bitterness: 'bitterness', sweetness: 'sweetness', smoothness: 'smoothness',
    umami: 'umami', body: 'body/richness', aroma_intensity: 'aroma intensity',
    astringency: 'astringency', finishText: 'a described finish'
  };

  function joinWithAnd(list) {
    if (list.length === 1) return list[0];
    if (list.length === 2) return list[0] + ' and ' + list[1];
    return list.slice(0, -1).join(', ') + ', and ' + list[list.length - 1];
  }

  // ---- Results-screen wording, built only from the customer's own answers
  // (never from product data) — see buildAnswerSummary/renderRecap. ----
  var PREP_ANSWER_PHRASE = {
    water: 'whisk it with water', latte: 'drink it as a latte',
    coffee: 'mix it with coffee', baking: 'use it for baking and desserts'
  };
  var FLAVOUR_ANSWER_PHRASE = {
    sweet_smooth: 'sweet, smooth flavours', savoury_rich: 'savoury flavours', strong_punchy: 'strong, punchy flavours'
  };
  var PREP_FOOT_WORD = { water: 'straight', latte: 'as a latte', coffee: 'with coffee' };
  var PREP_RECAP = { water: 'Whisked with water', latte: 'As a latte', coffee: 'Mixed with coffee', baking: 'In baking and desserts' };
  var BITTERNESS_RECAP = { enjoy: 'I enjoy it', little: 'A little is nice', gentle: 'I prefer it gentle', least: 'As little as possible' };
  var FLAVOUR_RECAP = { sweet_smooth: 'Sweet and smooth', savoury_rich: 'Savoury and rich', strong_punchy: 'Strong and punchy', not_sure: 'Not sure' };
  var FINISH_RECAP = { clean_crisp: 'A clean, crisp finish', dry: 'A slightly dry, mouth-drying feel', soft_sweet: 'A soft, sweet finish', no_preference: 'No preference', not_sure: 'Not sure' };
  var BUDGET_RECAP = { band1: 'Budget-friendly', band2: 'Mid-range', band3: 'Premium', no_preference: 'No preference' };

  // ---- "Why it suits you" chips — each maps a real, recorded rating (or
  // finish text) to one of the theme's existing trait icons, and only fires
  // when the customer actually asked about that trait AND the product has a
  // verified value supporting it. A trait the customer never asked about, or
  // one this product has no recorded value for, is never shown. ----
  var CHIP_ICON_NAME = {
    sweet: 'Sweet', mellow: 'Mellow', umami: 'Umami', rich: 'Rich',
    aromatic: 'Aromatic', bold: 'Bold', brisk: 'Brisk', clean: 'Clean'
  };
  var CHIP_RULES = [
    { icon: 'bold', rating: 'bitterness', min: 3.5, when: function (a) { return a.bitterness === 'enjoy' || a.bitterness === 'little'; } },
    { icon: 'mellow', rating: 'smoothness', min: 4, when: function (a) { return a.bitterness === 'gentle' || a.bitterness === 'least' || a.flavour === 'sweet_smooth'; } },
    { icon: 'sweet', rating: 'sweetness', min: 4, when: function (a) { return a.flavour === 'sweet_smooth' || a.finish === 'soft_sweet'; } },
    { icon: 'umami', rating: 'umami', min: 4, when: function (a) { return a.flavour === 'savoury_rich'; } },
    { icon: 'rich', rating: 'body', min: 4, when: function (a) { return a.flavour === 'savoury_rich' || a.flavour === 'strong_punchy'; } },
    { icon: 'aromatic', rating: 'aroma_intensity', min: 4, when: function (a) { return a.flavour === 'strong_punchy'; } },
    { icon: 'brisk', rating: 'astringency', min: 3.5, when: function (a) { return a.finish === 'dry'; } }
  ];

  function initSection(root) {
    var id = root.id.replace('wk-finder-', '');
    var dataEl = document.getElementById('wk-finder-data-' + id);
    var configEl = document.getElementById('wk-finder-config-' + id);
    if (!dataEl || !configEl) return;

    var products;
    var config;
    try {
      products = JSON.parse(dataEl.textContent);
      config = JSON.parse(configEl.textContent);
    } catch (e) {
      return; // malformed data — fail quietly rather than show a broken quiz
    }

    var quiz = root.querySelector('.wk-finder__quiz');
    var resultsPanel = root.querySelector('.wk-finder__results');
    var resultsInner = resultsPanel.querySelector('.wk-finder__results-inner');
    var liveRegion = root.querySelector('.wk-finder__live');
    var steps = Array.prototype.slice.call(root.querySelectorAll('.wk-finder__step'));
    var backBtn = root.querySelector('.wk-finder__back');
    var forwardBtn = root.querySelector('.wk-finder__forward');
    var startOverBtn = root.querySelector('.wk-finder__start-over');
    var progressFill = root.querySelector('.wk-finder__progress-fill');
    var progressCurrent = root.querySelector('.wk-finder__progress-current');
    var progressTotal = root.querySelector('.wk-finder__progress-total');
    var progressBar = root.querySelector('.wk-finder__progress');
    var cardBank = root.querySelector('.wk-finder__card-bank');
    var guideSource = root.querySelector('.wk-finder__guide');
    var iconBank = root.querySelector('.wk-finder__icon-bank');

    var reduceMotion = window.matchMedia && window.matchMedia('(prefers-reduced-motion: reduce)').matches;

    // Merchant-entered budget thresholds are plain numbers in the shop's
    // default currency (there's no currency-aware number setting in Shopify).
    // variant.price — and so pricePer30g, computed from it in Liquid — is
    // already converted to whatever currency the shopper is actively viewing
    // (confirmed: Liquid's money/variant objects are Markets-aware). So the
    // thresholds need the same conversion applied before they're compared
    // against pricePer30g, using Shopify's own published rate for the active
    // currency — never a hard-coded or invented rate.
    var currencyRate = (window.Shopify && window.Shopify.currency && parseFloat(window.Shopify.currency.rate)) || 1;
    var budgetBand1MaxActive = config.budgetBand1Max * currencyRate;
    var budgetBand2MaxActive = config.budgetBand2Max * currencyRate;

    var answers = { prep: null, bitterness: null, flavour: null, finish: null, budget: null };
    var currentStep = 0;

    // Step count comes from the actual steps rendered in the DOM, never a
    // hard-coded number — so removing/adding a question keeps working.
    if (progressTotal) progressTotal.textContent = String(steps.length);
    progressBar.setAttribute('aria-valuemax', String(steps.length));

    fillBudgetLabels();
    bindOptionButtons();
    bindNav();
    showStep(0);

    // ---- Budget option labels, from Liquid-formatted money so JS never formats currency ----
    function fillBudgetLabels() {
      var b1 = formatMoneyPerThirty(budgetBand1MaxActive);
      var b2 = formatMoneyPerThirty(budgetBand2MaxActive);
      setBandLabel(1, 'Up to ' + b1 + ' / 30g');
      setBandLabel(2, b1 + '–' + b2 + ' / 30g');
      setBandLabel(3, 'Above ' + b2 + ' / 30g');
    }

    function setBandLabel(n, text) {
      var el = root.querySelector('.wk-finder__option-sub[data-band="' + n + '"]');
      if (el) el.textContent = text;
    }

    // Thresholds come from the merchant as plain decimal-currency numbers (no
    // currency symbol attached). Format them with the shop's own money_format
    // template (passed through from Liquid as config.moneyFormat) rather than
    // guessing at symbol/separator placement — this is Shopify's documented
    // {{amount}}-style placeholder format, the same one storefronts use.
    function formatMoneyPerThirty(amount) {
      return formatMoney(Math.round(amount * 100), config.moneyFormat);
    }

    function formatMoney(cents, format) {
      var formatString = format || '${{amount}}';
      var placeholderMatch = formatString.match(/\{\{\s*(\w+)\s*\}\}/);
      if (!placeholderMatch) return formatString;

      function withDelimiters(number, precision, thousands, decimal) {
        precision = typeof precision === 'undefined' ? 2 : precision;
        thousands = typeof thousands === 'undefined' ? ',' : thousands;
        decimal = typeof decimal === 'undefined' ? '.' : decimal;
        if (isNaN(number) || number === null) return '0';
        var fixed = (number / 100).toFixed(precision);
        var parts = fixed.split('.');
        var wholePart = parts[0].replace(/(\d)(?=(\d{3})+(?!\d))/g, '$1' + thousands);
        var centsPart = parts[1] ? decimal + parts[1] : '';
        return wholePart + centsPart;
      }

      var value;
      switch (placeholderMatch[1]) {
        case 'amount': value = withDelimiters(cents, 2); break;
        case 'amount_no_decimals': value = withDelimiters(cents, 0); break;
        case 'amount_with_comma_separator': value = withDelimiters(cents, 2, '.', ','); break;
        case 'amount_no_decimals_with_comma_separator': value = withDelimiters(cents, 0, '.', ','); break;
        case 'amount_with_space_separator': value = withDelimiters(cents, 2, ' ', '.'); break;
        case 'amount_no_decimals_with_space_separator': value = withDelimiters(cents, 0, ' ', ''); break;
        default: value = withDelimiters(cents, 2);
      }
      return formatString.replace(placeholderMatch[0], value);
    }

    // ---- Option selection (ARIA radiogroup pattern: roving tabindex + arrow keys) ----
    function bindOptionButtons() {
      steps.forEach(function (step) {
        var group = step.querySelector('.wk-finder__options');
        var options = Array.prototype.slice.call(group.querySelectorAll('.wk-finder__option'));
        options.forEach(function (btn, i) {
          btn.tabIndex = i === 0 ? 0 : -1;
          btn.addEventListener('click', function () { selectOption(step, options, btn); });
          btn.addEventListener('keydown', function (e) { handleOptionKeydown(e, options, i); });
        });
      });
    }

    function handleOptionKeydown(e, options, i) {
      var next = null;
      if (e.key === 'ArrowDown' || e.key === 'ArrowRight') next = (i + 1) % options.length;
      else if (e.key === 'ArrowUp' || e.key === 'ArrowLeft') next = (i - 1 + options.length) % options.length;
      else if (e.key === 'Home') next = 0;
      else if (e.key === 'End') next = options.length - 1;
      if (next === null) return;
      e.preventDefault();
      options.forEach(function (o) { o.tabIndex = -1; });
      options[next].tabIndex = 0;
      options[next].focus();
      selectOption(options[next].closest('.wk-finder__step'), options, options[next]);
    }

    function selectOption(step, options, chosen) {
      options.forEach(function (o) { o.setAttribute('aria-checked', 'false'); o.classList.remove('is-selected'); });
      chosen.setAttribute('aria-checked', 'true');
      chosen.classList.add('is-selected');
      answers[step.dataset.question] = chosen.dataset.value;
      updateNavState();
    }

    // ---- Navigation ----
    // Single forward button throughout: on every question except the last
    // it advances to the next one; on whichever question is actually last
    // in the DOM (so removing a question, e.g. "finish", keeps working with
    // no code change) it shows the results instead. The label follows the
    // same rule, so there's never a second, separately-hidden button that
    // can be left showing by a CSS conflict.
    function bindNav() {
      backBtn.addEventListener('click', function () { if (currentStep > 0) showStep(currentStep - 1); });
      forwardBtn.addEventListener('click', function () {
        if (currentStep === steps.length - 1) showResults();
        else showStep(currentStep + 1);
      });
      startOverBtn.addEventListener('click', startOver);
    }

    function showStep(index) {
      currentStep = index;
      steps.forEach(function (s, i) { s.classList.toggle('is-active', i === index); });
      var stepNum = index + 1;
      progressCurrent.textContent = String(stepNum);
      progressBar.setAttribute('aria-valuenow', String(stepNum));
      var pct = Math.round((stepNum / steps.length) * 100);
      progressFill.style.transition = reduceMotion ? 'none' : '';
      progressFill.style.width = pct + '%';
      backBtn.disabled = index === 0;
      updateNavState();
      var legend = steps[index].querySelector('.wk-finder__question');
      if (legend) { legend.setAttribute('tabindex', '-1'); legend.focus({ preventScroll: true }); }
      announce('Step ' + stepNum + ' of ' + steps.length + ': ' + (legend ? legend.textContent : ''));
    }

    function updateNavState() {
      var step = steps[currentStep];
      var isLast = currentStep === steps.length - 1;
      var required = step.dataset.required === 'true';
      var answered = !!answers[step.dataset.question];
      forwardBtn.disabled = required && !answered;
      if (isLast) forwardBtn.textContent = 'See my match';
      else if (!required && !answered) forwardBtn.textContent = 'Skip';
      else forwardBtn.textContent = 'Next';
    }

    function announce(text) {
      liveRegion.textContent = text;
    }

    function startOver() {
      answers = { prep: null, bitterness: null, flavour: null, finish: null, budget: null };
      root.querySelectorAll('.wk-finder__option').forEach(function (o) {
        o.setAttribute('aria-checked', 'false');
        o.classList.remove('is-selected');
      });
      resultsPanel.hidden = true;
      quiz.hidden = false;
      showStep(0);
    }

    // ================= SCORING =================

    function showResults() {
      var outcome = computeOutcome();
      renderResults(outcome);
      quiz.hidden = true;
      resultsPanel.hidden = false;

      // Gentle fade-and-rise each time results are (re)shown — the keyframe
      // itself is skipped under prefers-reduced-motion (see matcha-finder.css).
      // Re-triggering it on every render (not just the first) needs a forced
      // reflow between removing and re-adding the class.
      resultsPanel.classList.remove('is-visible');
      void resultsPanel.offsetWidth;
      resultsPanel.classList.add('is-visible');

      // Focus the product name so screen readers announce the match; if this
      // result has no single product name (low confidence / no candidates),
      // fall back to the results panel itself, as before.
      var focusTarget = resultsInner.querySelector('[data-wk-focus]') || resultsPanel;
      focusTarget.setAttribute('tabindex', '-1');
      focusTarget.focus({ preventScroll: true });

      if (config.designMode) renderDebugPanel(outcome);
    }

    function computeOutcome() {
      var warnings = [];

      // 1) Preparation gate — the only hard filter.
      var prepKey = answers.prep; // water | latte | coffee | baking
      var candidates = products.filter(function (p) {
        if (prepKey === 'baking') return p.bakingSuitable === true;
        var prep = p.prep[prepKey];
        if (!prep || prep.status === null) return false; // unknown ≠ suitable
        if (prep.status === config.notRecommendedStatus) return false;
        if (config.recognisedStatuses.indexOf(prep.status) === -1) {
          warnings.push(p.handle + ': unrecognised preparation status "' + prep.status + '" — treated as not suitable.');
          return false;
        }
        return true;
      });

      if (candidates.length === 0) {
        return { noCandidates: true, prepKey: prepKey, warnings: warnings };
      }

      // 2) Score each candidate on fixed, question-level point pools.
      var answerable = { bitterness: false, flavour: false, finish: false };
      // Each candidate gets two SEPARATE measures, never blended into one:
      //   preferenceFit ("earned") — how well its VERIFIED ratings match what
      //     was asked, using only non-null data, at a fixed per-question pool.
      //   evidenceCoverage — which of the customer's stated preferences this
      //     product actually HAS verified data for. A product is never allowed
      //     to look like a stronger match just because a competitor has less
      //     data — missing data earns nothing, but it's also surfaced
      //     explicitly (missingAttrs) rather than silently dropped, so a
      //     "best match" that's missing evidence is labelled as qualified,
      //     not presented as a confident, fully-verified pick.
      var scored = candidates.map(function (p) {
        var earned = 0;
        var possible = 0; // this product's own ceiling, used only for the confidence check below
        var missingAttrs = {};

        // Bitterness (Q2)
        if (answers.bitterness && BITTERNESS_TARGET.hasOwnProperty(answers.bitterness)) {
          answerable.bitterness = true;
          if (p.ratings.bitterness !== null) {
            var target = BITTERNESS_TARGET[answers.bitterness];
            var closeness = Math.max(0, 1 - Math.abs(target - p.ratings.bitterness) / 5);
            earned += POOL.bitterness * closeness;
            possible += POOL.bitterness;
          } else {
            missingAttrs.bitterness = true;
          }
        }

        // Flavour (Q3) — each answer maps to up to two attributes, each worth half the pool.
        var half = POOL.flavour / 2;
        var flavourAttrs = [];
        if (answers.flavour === 'sweet_smooth') flavourAttrs = ['sweetness', 'smoothness'];
        else if (answers.flavour === 'savoury_rich') flavourAttrs = ['umami', 'body'];
        else if (answers.flavour === 'strong_punchy') flavourAttrs = ['aroma_intensity', 'body'];
        if (flavourAttrs.length) {
          answerable.flavour = true;
          flavourAttrs.forEach(function (attr) {
            if (p.ratings[attr] !== null) {
              var high = Math.max(0, p.ratings[attr] / 5); // these are all "more = more of this" attributes
              earned += half * high;
              possible += half;
            } else {
              missingAttrs[attr] = true;
            }
          });
        }

        // Finish (Q4) — smaller pool, skipped entirely if unanswered/no preference.
        if (answers.finish === 'clean_crisp') {
          answerable.finish = true;
          if (p.finishText !== null) {
            possible += POOL.finish;
            if (/clean/i.test(p.finishText)) earned += POOL.finish;
          } else {
            missingAttrs.finishText = true;
          }
        } else if (answers.finish === 'dry') {
          answerable.finish = true;
          if (p.ratings.astringency !== null) {
            possible += POOL.finish;
            earned += POOL.finish * Math.max(0, 1 - Math.abs(FINISH_DRY_TARGET - p.ratings.astringency) / 5);
          } else {
            missingAttrs.astringency = true;
          }
        } else if (answers.finish === 'soft_sweet') {
          answerable.finish = true;
          if (p.ratings.sweetness !== null) {
            possible += POOL.finish;
            earned += POOL.finish * Math.max(0, 1 - Math.abs(FINISH_SWEET_TARGET - p.ratings.sweetness) / 5);
          } else {
            missingAttrs.sweetness = true; // same key as Q3's gap — naturally de-duplicated below
          }
          // Deliberately NOT also scoring Q3's sweetness contribution again here —
          // this is Q4's own separate pool, so it adds to (not duplicates) Q3's.
        }

        var prep = prepKey === 'baking' ? null : p.prep[prepKey];
        var prepBonus = prep && PREP_RANK[prep.status] ? PREP_RANK[prep.status] : 0;

        var missingList = Object.keys(missingAttrs).map(function (k) { return ATTR_LABEL[k]; });
        var gapSummary = missingList.length
          ? 'For ' + joinWithAnd(missingList) + ', we don’t have a verified rating for this matcha yet, so we can’t confirm how it compares on that.'
          : null;

        return { product: p, earned: earned, possible: possible, prepBonus: prepBonus, hasGap: missingList.length > 0, gapSummary: gapSummary };
      });

      scored.sort(function (a, b) {
        return (b.earned + b.prepBonus) - (a.earned + a.prepBonus)
          || b.prepBonus - a.prepBonus
          || priceOrInfinity(a.product) - priceOrInfinity(b.product)
          || a.product.title.localeCompare(b.product.title);
      });

      // 3) Confidence check — don't present a confident pick built on thin data.
      var answerablePoints = (answerable.bitterness ? POOL.bitterness : 0)
        + (answerable.flavour ? POOL.flavour : 0)
        + (answerable.finish ? POOL.finish : 0);
      var top = scored[0];
      var coverage = answerablePoints > 0 ? top.possible / answerablePoints : 0;
      var secondGap = scored.length > 1 ? (top.earned + top.prepBonus) - (scored[1].earned + scored[1].prepBonus) : Infinity;
      // Only meaningful when there's an actual choice to be unsure between —
      // with a single candidate there's nothing to compare, so we fall through
      // to the normal result (its explanation simply omits anything unknown).
      var lowConfidence = scored.length > 1 && (
        (answerablePoints > 0 && coverage < 0.34)
        || (secondGap <= 5 && coverage < 0.5)
      );

      // 4) Budget — a filter/sort preference, applied after taste ranking, never silently ignored.
      var budgetOutcome = applyBudget(scored);

      return {
        prepKey: prepKey,
        scored: scored,
        lowConfidence: lowConfidence,
        warnings: warnings,
        budget: budgetOutcome
      };
    }

    function priceOrInfinity(p) {
      return typeof p.pricePer30g === 'number' ? p.pricePer30g : Infinity;
    }

    function bandOf(p) {
      if (typeof p.pricePer30g !== 'number') return null;
      if (p.pricePer30g <= budgetBand1MaxActive) return 1;
      if (p.pricePer30g <= budgetBand2MaxActive) return 2;
      return 3;
    }

    function applyBudget(scored) {
      if (answers.budget === 'no_preference' || !answers.budget) {
        return { mode: 'no_preference', best: scored[0], alt: scored[1] || null };
      }
      var wantBand = { band1: 1, band2: 2, band3: 3 }[answers.budget];
      var inBand = scored.filter(function (s) { return bandOf(s.product) === wantBand; });
      if (inBand.length) {
        return { mode: 'in_band', band: wantBand, best: inBand[0], alt: inBand[1] || null };
      }
      // Nothing in the chosen band: find the closest-by-price suitable product instead.
      var withPrice = scored.filter(function (s) { return typeof s.product.pricePer30g === 'number'; });
      if (!withPrice.length) {
        return { mode: 'no_price_data', best: scored[0], alt: scored[1] || null };
      }
      var edges = { 1: [0, budgetBand1MaxActive], 2: [budgetBand1MaxActive, budgetBand2MaxActive], 3: [budgetBand2MaxActive, Infinity] };
      var lo = edges[wantBand][0], hi = edges[wantBand][1];
      withPrice.sort(function (a, b) {
        return distanceToRange(a.product.pricePer30g, lo, hi) - distanceToRange(b.product.pricePer30g, lo, hi)
          || (b.earned + b.prepBonus) - (a.earned + a.prepBonus);
      });
      var closest = withPrice[0];
      var direction = closest.product.pricePer30g > hi ? 'above' : 'below';
      var diff = direction === 'above' ? closest.product.pricePer30g - hi : lo - closest.product.pricePer30g;
      return { mode: 'out_of_band', band: wantBand, best: closest, direction: direction, diff: diff };
    }

    function distanceToRange(value, lo, hi) {
      if (value < lo) return lo - value;
      if (value > hi) return value - hi;
      return 0;
    }

    // ================= RENDERING =================

    // One result layout for every path: a single "main" product gets the
    // prominent card (photo, name, explanation, chips, price, button), and
    // any further candidates go underneath as smaller "Also worth trying"
    // cards — whether that's a genuine best-match-plus-alternative, or a
    // tied/uncertain group where there's no single confident winner. Only
    // two things vary by path: which candidates are chosen (unchanged
    // ranking/budget logic, untouched here) and the eyebrow wording, so a
    // tied result is never dressed up as "Your best match".
    function renderResults(outcome) {
      resultsInner.innerHTML = '';

      if (outcome.noCandidates) {
        var msg = document.createElement('p');
        msg.className = 'wk-finder__empty';
        msg.textContent = "We don't currently have a confirmed matcha for that choice — please check back soon, or get in touch and we'll help directly.";
        resultsInner.appendChild(msg);
        return;
      }

      var mainEntry, altEntries, eyebrowText, extraNote;

      if (outcome.lowConfidence) {
        eyebrowText = 'Matcha to try';
        mainEntry = outcome.scored[0];
        altEntries = outcome.scored.slice(1, 3);
        extraNote = null;
      } else {
        var budget = outcome.budget;
        eyebrowText = 'Your best match';
        mainEntry = budget.best;
        altEntries = (budget.alt && budget.mode !== 'out_of_band') ? [budget.alt] : [];
        extraNote = budget.mode === 'out_of_band' ? budgetNote(budget) : null;
      }

      var eyebrow = document.createElement('p');
      eyebrow.className = 'wk-finder__result-eyebrow';
      eyebrow.textContent = eyebrowText;
      resultsInner.appendChild(eyebrow);

      if (extraNote) resultsInner.appendChild(extraNote);

      resultsInner.appendChild(renderBestMatch(mainEntry));

      if (altEntries.length) {
        var altBlock = document.createElement('div');
        altBlock.className = 'wk-finder__result-block';
        var altLabel = document.createElement('p');
        altLabel.className = 'wk-finder__result-block-label';
        altLabel.textContent = 'Also worth trying';
        altBlock.appendChild(altLabel);
        var list = document.createElement('div');
        list.className = 'wk-finder__option-list';
        altEntries.forEach(function (entry) { list.appendChild(renderAltCard(entry)); });
        altBlock.appendChild(list);
        resultsInner.appendChild(altBlock);
      }

      var recap = renderRecap();
      if (recap) resultsInner.appendChild(recap);

      // Water-only, and shown regardless of how many candidates there are
      // or how confident the result is — see renderGuide.
      var guide = renderGuide();
      if (guide) resultsInner.appendChild(guide);
    }

    function budgetNote(budget) {
      var p = document.createElement('p');
      p.className = 'wk-finder__budget-note';
      var bandLabel = { 1: 'Budget-friendly', 2: 'Mid-range', 3: 'Premium' }[budget.band];
      var diffFormatted = formatMoneyPerThirty(budget.diff);
      p.textContent = 'Nothing suitable fell inside your ' + bandLabel + ' range today, so here is the closest match — it is about ' + diffFormatted + ' ' + budget.direction + ' that range, per 30g.';
      return p;
    }

    // ---- Main result card (used for both a confident best match and the
    // top-ranked candidate in a tied/uncertain result): Japanese name +
    // circular photo, one explanatory sentence built from the customer's
    // own answers plus the product's own verified Summary, "why it suits
    // you" chips, the actual price, and the action row. ----
    function renderBestMatch(scoredEntry) {
      var p = scoredEntry.product;
      var wrap = document.createElement('div');
      wrap.className = 'wk-finder__result-best';

      var head = document.createElement('div');
      head.className = 'wk-finder__result-head';
      head.appendChild(renderNameBlock(p, true));

      var avatar = document.createElement('a');
      avatar.className = 'wk-finder__result-avatar';
      avatar.href = p.url;
      avatar.tabIndex = -1;
      avatar.setAttribute('aria-hidden', 'true');
      var img = cardBank.querySelector('.wk-finder__card-source[data-handle="' + cssEscape(p.handle) + '"] img');
      if (img) avatar.appendChild(img.cloneNode(true));
      head.appendChild(avatar);

      wrap.appendChild(head);

      var about = buildAbout(p);
      if (about) {
        var aboutEl = document.createElement('p');
        aboutEl.className = 'wk-finder__result-about';
        aboutEl.textContent = about;
        wrap.appendChild(aboutEl);
      }

      var chips = renderChips(p);
      if (chips) wrap.appendChild(chips);

      // Actual purchasable price only — no pack size or per-30g figure.
      // Those are still computed and used internally for budget matching
      // (see computeOutcome/bandOf/applyBudget); this is a display choice.
      var priceLine = document.createElement('p');
      priceLine.className = 'wk-finder__result-price';
      priceLine.textContent = p.packPriceFormatted;
      wrap.appendChild(priceLine);

      var foot = document.createElement('div');
      foot.className = 'wk-finder__result-foot';
      var cta = document.createElement('a');
      cta.className = 'wk-finder__pill-btn';
      cta.href = p.url;
      cta.textContent = 'View this matcha';
      foot.appendChild(cta);
      var prepFoot = buildPrepFoot(p);
      if (prepFoot) foot.appendChild(prepFoot);
      wrap.appendChild(foot);

      return wrap;
    }

    // ---- Alternative card (also used for every candidate past the first in
    // a tied/uncertain result): the same component as the main card, just
    // smaller — thumbnail, name, concise description, chips, actual price,
    // link. Same structure, same order, every result path. ----
    function renderAltCard(scoredEntry) {
      var p = scoredEntry.product;
      var card = document.createElement('div');
      card.className = 'wk-finder__alt';

      var head = document.createElement('div');
      head.className = 'wk-finder__alt-head';
      head.appendChild(renderNameBlock(p, false));

      var thumb = document.createElement('a');
      thumb.className = 'wk-finder__result-avatar wk-finder__result-avatar--small';
      thumb.href = p.url;
      thumb.tabIndex = -1;
      thumb.setAttribute('aria-hidden', 'true');
      var thumbImg = cardBank.querySelector('.wk-finder__card-source[data-handle="' + cssEscape(p.handle) + '"] img');
      if (thumbImg) thumb.appendChild(thumbImg.cloneNode(true));
      head.appendChild(thumb);

      card.appendChild(head);

      if (p.summary) {
        var summaryEl = document.createElement('p');
        summaryEl.className = 'wk-finder__alt-summary';
        summaryEl.textContent = p.summary;
        card.appendChild(summaryEl);
      }

      var chips = renderChips(p);
      if (chips) card.appendChild(chips);

      // Actual purchasable price only — matches the main card's price display.
      var priceLine = document.createElement('p');
      priceLine.className = 'wk-finder__alt-price';
      priceLine.textContent = p.packPriceFormatted;
      card.appendChild(priceLine);

      var cta = document.createElement('a');
      cta.className = 'wk-finder__pill-btn wk-finder__pill-btn--ghost wk-finder__pill-btn--small';
      cta.href = p.url;
      cta.textContent = 'View matcha';
      card.appendChild(cta);

      return card;
    }

    // Japanese name as the heading (falling back to the product's own title
    // when there's no Tasting Profile to read a Japanese name from), with
    // the romanised title underneath only when a Japanese name exists —
    // showing the same text twice otherwise would be pointless. Liquid
    // already strips a trailing "(Romaji)" when it reads this from
    // admin_name (see sections/matcha-finder.liquid), but it's stripped
    // again here too, defensively, so the heading can never show the
    // romaji twice regardless of how that field is formatted.
    function renderNameBlock(p, isBest) {
      var wrap = document.createElement('div');
      wrap.className = 'wk-finder__result-name';
      var headingText = (p.japaneseName || p.title).replace(/\s*\([^)]*\)\s*$/, '');
      var heading = document.createElement(isBest ? 'h2' : 'h4');
      heading.textContent = headingText;
      if (headingText.length > 6) heading.classList.add('is-long');
      if (isBest) heading.setAttribute('data-wk-focus', '');
      wrap.appendChild(heading);
      if (p.japaneseName) {
        var romaji = document.createElement('p');
        romaji.className = isBest ? 'wk-finder__result-romaji' : 'wk-finder__alt-romaji';
        romaji.textContent = p.title;
        wrap.appendChild(romaji);
      }
      return wrap;
    }

    // Sentence 1: the customer's own answers, in plain English — never
    // anything about the product. Sentence 2: the product's own verified
    // Summary field, shown as-is (nothing is paraphrased or invented).
    function buildAbout(p) {
      var parts = [];
      var answerSummary = buildAnswerSummary();
      if (answerSummary) parts.push(answerSummary);
      if (p.summary) parts.push(p.summary);
      return parts.join(' ');
    }

    function buildAnswerSummary() {
      var prepPhrase = PREP_ANSWER_PHRASE[answers.prep];
      if (!prepPhrase) return '';
      var sentence = "You'll mostly " + prepPhrase;
      var flavourPhrase = FLAVOUR_ANSWER_PHRASE[answers.flavour];
      if (flavourPhrase) sentence += ', and you like ' + flavourPhrase;
      return sentence + '.';
    }

    // Small muted "Great <strong>straight</strong>"-style note beside the
    // button, built only from this product's own recorded status for the
    // chosen preparation. Omitted for baking (no such status exists) and
    // whenever there's no recorded status to report.
    function buildPrepFoot(p) {
      var word = PREP_FOOT_WORD[answers.prep];
      if (!word) return null;
      var prep = p.prep[answers.prep];
      if (!prep || !prep.status) return null;
      var el = document.createElement('span');
      el.className = 'wk-finder__result-as';
      el.appendChild(document.createTextNode(prep.status + ' '));
      var strong = document.createElement('strong');
      strong.textContent = word;
      el.appendChild(strong);
      return el;
    }

    // At most 3 chips, each built from a real rating/finish-text this
    // product has AND a question the customer actually answered — see
    // CHIP_RULES above. A product with no supporting data for anything the
    // customer asked about simply gets no chips, rather than a guess.
    function renderChips(p) {
      var keys = [];
      CHIP_RULES.forEach(function (rule) {
        if (keys.length >= 3 || keys.indexOf(rule.icon) !== -1) return;
        if (!rule.when(answers)) return;
        var val = p.ratings[rule.rating];
        if (val === null || val < rule.min) return;
        keys.push(rule.icon);
      });
      if (keys.length < 3 && answers.finish === 'clean_crisp' && p.finishText && /clean/i.test(p.finishText)) {
        keys.push('clean');
      }
      if (!keys.length) return null;

      var wrap = document.createElement('div');
      wrap.className = 'wk-finder__chips';
      var label = document.createElement('span');
      label.className = 'wk-finder__chips-label';
      label.textContent = 'Why it suits you';
      wrap.appendChild(label);
      keys.forEach(function (key) {
        var chip = document.createElement('span');
        chip.className = 'wk-finder__chip';
        var iconSource = iconBank ? iconBank.querySelector('.wk-finder__icon-source[data-icon="' + key + '"]') : null;
        if (iconSource && iconSource.firstElementChild) {
          chip.appendChild(iconSource.firstElementChild.cloneNode(true));
        }
        var name = document.createElement('span');
        name.textContent = CHIP_ICON_NAME[key];
        chip.appendChild(name);
        wrap.appendChild(chip);
      });
      return wrap;
    }

    // A plain recap of the customer's own answers — not a product match,
    // just what they told us, so they can check it at a glance.
    function renderRecap() {
      var rows = [
        ['Preparation', PREP_RECAP[answers.prep]],
        ['Bitterness', BITTERNESS_RECAP[answers.bitterness]],
        ['Flavour', FLAVOUR_RECAP[answers.flavour]],
        ['Finish', FINISH_RECAP[answers.finish]],
        ['Budget', BUDGET_RECAP[answers.budget]]
      ].filter(function (row) { return !!row[1]; });
      if (!rows.length) return null;

      var block = document.createElement('div');
      block.className = 'wk-finder__result-block';
      var label = document.createElement('p');
      label.className = 'wk-finder__result-block-label';
      label.textContent = "What you're looking for";
      block.appendChild(label);
      var list = document.createElement('ul');
      list.className = 'wk-finder__recap';
      rows.forEach(function (row) {
        var li = document.createElement('li');
        var key = document.createElement('span');
        key.className = 'wk-finder__recap-key';
        key.textContent = row[0];
        var val = document.createElement('span');
        val.className = 'wk-finder__recap-val';
        val.textContent = row[1];
        li.appendChild(key);
        li.appendChild(val);
        list.appendChild(li);
      });
      block.appendChild(list);
      return block;
    }

    // The only verified recipe content we have is the water-whisking guide,
    // so it's shown for that preparation only. For latte/coffee/baking there
    // is no verified recipe to show, so nothing is invented in its place.
    function renderGuide() {
      if (answers.prep !== 'water') return null;
      var wrap = document.createElement('div');
      wrap.className = 'wk-finder__result-block wk-finder__guide-wrap';
      var heading = document.createElement('p');
      heading.className = 'wk-finder__result-block-label';
      heading.textContent = 'How to make it';
      wrap.appendChild(heading);
      if (guideSource) {
        var clone = guideSource.firstElementChild ? guideSource.cloneNode(true) : null;
        if (clone) { clone.hidden = false; wrap.appendChild(clone); }
      }
      return wrap;
    }

    function renderDebugPanel(outcome) {
      var existing = root.querySelector('.wk-finder__debug');
      if (existing) existing.remove();
      var details = document.createElement('details');
      details.className = 'wk-finder__debug';
      var summary = document.createElement('summary');
      summary.textContent = 'Matcha Finder debug (only visible in the theme editor)';
      details.appendChild(summary);

      if (outcome.warnings && outcome.warnings.length) {
        var wHead = document.createElement('p');
        wHead.textContent = 'Data warnings:';
        details.appendChild(wHead);
        var wList = document.createElement('ul');
        outcome.warnings.forEach(function (w) { var li = document.createElement('li'); li.textContent = w; wList.appendChild(li); });
        details.appendChild(wList);
      }

      if (outcome.scored) {
        var note = document.createElement('p');
        note.textContent = 'Preference fit and evidence coverage are tracked separately — Earned/Possible is fit on verified data only; Gap lists what’s unverified for that product.';
        details.appendChild(note);
        var table = document.createElement('table');
        var head = document.createElement('tr');
        ['Product', 'Earned (fit)', 'Possible (fit ceiling)', 'Prep bonus', 'Band', 'Evidence gap'].forEach(function (h) {
          var th = document.createElement('th'); th.textContent = h; head.appendChild(th);
        });
        table.appendChild(head);
        outcome.scored.forEach(function (s) {
          var row = document.createElement('tr');
          [s.product.title, s.earned.toFixed(1), s.possible, s.prepBonus, bandOf(s.product) || 'unknown', s.gapSummary || 'none'].forEach(function (v) {
            var td = document.createElement('td'); td.textContent = v; row.appendChild(td);
          });
          table.appendChild(row);
        });
        details.appendChild(table);
      }
      resultsInner.appendChild(details);
    }

    function cssEscape(str) {
      return window.CSS && CSS.escape ? CSS.escape(str) : str.replace(/"/g, '\\"');
    }
  }

  function init() {
    document.querySelectorAll('.wk-finder').forEach(initSection);
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }
})();
